import React, { useState, useEffect, useRef, useCallback } from 'react';
import BaseChart from './BaseChart';
import { getAuthToken } from '../../utils/authUtils';
import { toast } from 'react-toastify';
import KalmanFilter from '../../utils/KalmanFilter';
import chartSyncActivator from '../../utils/ChartSyncActivator';
import './ChartStyles.css';
import { formatChartTimeLabel, formatChartTooltipTime, validateDateRange } from '../../utils/DateUtils';
import { Chart as ChartJS } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

// Регистрируем плагин аннотаций
ChartJS.register(annotationPlugin);

// Активируем синхронизацию графиков при импорте компонента
if (!chartSyncActivator.initialized) {
  chartSyncActivator.initialize();
}

// Статические параметры фильтра Калмана
const KALMAN_PROCESS_NOISE = 0.0275;  // Q - шум процесса
const KALMAN_MEASUREMENT_NOISE = 2.10; // R - шум измерения

const FuelChart = ({ vehicle, startDate: propsStartDate, endDate: propsEndDate }) => {
  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFiltered, setShowFiltered] = useState(true);
  const [fuelEvents, setFuelEvents] = useState({ refills: [], drains: [] }); // Заправки и сливы
  const [kalmanParams, setKalmanParams] = useState({
    processNoise: KALMAN_PROCESS_NOISE,
    measurementNoise: KALMAN_MEASUREMENT_NOISE
  });
  const abortControllerRef = useRef(null);
  const kalmanFilterRef = useRef(new KalmanFilter(
    KALMAN_PROCESS_NOISE, 
    KALMAN_MEASUREMENT_NOISE
  ));
  
  // Состояние для хранения дат, которые будут синхронизироваться с localStorage
  const [startDate, setStartDate] = useState(propsStartDate || null);
  const [endDate, setEndDate] = useState(propsEndDate || null);
  
  // Функция для обеспечения валидных дат
  const ensureValidDate = useCallback((dateInput) => {
    try {
      // Если дата уже является объектом Date и она валидна
      if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
        return dateInput;
      }
      
      // Если дата - строка
      if (typeof dateInput === 'string') {
        // Проверяем, не является ли строка пустой или невалидной
        if (!dateInput || dateInput === 'Invalid Date') {
          console.warn('Получена невалидная строка даты:', dateInput);
          return new Date();
        }
        
        // Пробуем стандартный метод
        const newDate = new Date(dateInput);
        if (!isNaN(newDate.getTime())) {
          return newDate;
        }
      }
      
      // Если дата - timestamp (число)
      if (typeof dateInput === 'number') {
        const newDate = new Date(dateInput);
        if (!isNaN(newDate.getTime())) {
          return newDate;
        }
      }
      
      // Если не удалось создать валидную дату, возвращаем текущую
      console.warn('Невозможно создать валидную дату из:', dateInput);
      return new Date();
    } catch (error) {
      console.error('Ошибка при обработке даты:', error, dateInput);
      return new Date();
    }
  }, []);
  
  // Обновляем startDate и endDate, когда меняются props
  useEffect(() => {
    if (propsStartDate) {
      const validStartDate = ensureValidDate(propsStartDate);
      setStartDate(validStartDate);
    }
    
    if (propsEndDate) {
      const validEndDate = ensureValidDate(propsEndDate);
      setEndDate(validEndDate);
    }
  }, [propsStartDate, propsEndDate, ensureValidDate]);
  
  // Функция получения дат из localStorage
  const getDateRangeFromLocalStorage = useCallback(() => {
    try {
      const storedRange = localStorage.getItem('lastDateRange');
      if (storedRange) {
        const parsed = JSON.parse(storedRange);
        return {
          startDate: parsed.startDate ? ensureValidDate(parsed.startDate) : null,
          endDate: parsed.endDate ? ensureValidDate(parsed.endDate) : null,
          updateTimestamp: parsed.updateTimestamp
        };
      }
    } catch (e) {
      console.warn('Ошибка при чтении диапазона дат из localStorage:', e);
    }
    return { startDate: null, endDate: null };
  }, [ensureValidDate]);
  
  // Загрузка данных при изменении дат или ТС - оптимизирована с useCallback
  const fetchFuelData = useCallback(async () => {
    if (!vehicle || !startDate || !endDate) {
      console.warn('FuelChart: Невозможно загрузить данные - отсутствует транспорт или даты');
      return;
    }

    // Если уже идет загрузка, отменяем предыдущий запрос
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Создаем новый AbortController для возможности отмены запроса
    abortControllerRef.current = new AbortController();
    
    setIsLoading(true);
    setError(null);
    
    // Проверяем и исправляем диапазон дат
    const { startDate: validStartDate, endDate: validEndDate } = validateDateRange(startDate, endDate);
    
    try {
      // Форматируем даты для запроса в формате ISO без миллисекунд
      const formatDateForAPI = (date) => {
        if (!date) return '';
        // Преобразуем в формат YYYY-MM-DDThh:mm:ssZ (без миллисекунд)
        return date.toISOString().split('.')[0] + 'Z';
      };
      
      const startISO = formatDateForAPI(validStartDate);
      const endISO = formatDateForAPI(validEndDate);
      
      // Проверяем наличие IMEI
      if (!vehicle.imei) {
        throw new Error('IMEI транспортного средства не определен');
      }
      
      // URL для запроса данных о топливе
      const url = `/api/fuel/${vehicle.imei}/liters?startTime=${startISO}&endTime=${endISO}`;
      
      console.log('FuelChart: Запрос данных топлива:', {
        imei: vehicle.imei,
        startDate: startISO,
        endDate: endISO
      });
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        signal: abortControllerRef.current.signal
      });
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Неизвестная ошибка');
        throw new Error(`Ошибка получения данных: ${response.status} ${response.statusText}. ${errorText}`);
      }
      
      const data = await response.json();
      console.log('FuelChart: Получены данные о топливе:', data);
      
      // Проверка на пустой ответ
      if (!data || !data.data || !Array.isArray(data.data)) {
        console.warn('FuelChart: Получен неверный формат данных:', data);
        setChartData([]);
        setChartLabels([]);
        setFilteredData([]);
        return;
      }
      
      // Обрабатываем полученные данные
      processFuelData(data);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('FuelChart: Запрос был отменен');
        return;
      }
      
      console.error('FuelChart: Ошибка при загрузке данных о топливе:', error);
      setError(`Ошибка: ${error.message}`);
      toast.error(`Не удалось загрузить данные о топливе: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [vehicle, startDate, endDate]);
  
  // Функция анализа топливных событий (заправки и сливы)
  const analyzeFuelEvents = useCallback((data, labels) => {
    if (!data || data.length < 3) {
      return { refills: [], drains: [] };
    }
    
    const refills = [];
    const drains = [];
    
    // Параметры для определения событий
    const MIN_REFILL_AMOUNT = 5; // Минимальный объем заправки (литры)
    const MIN_DRAIN_AMOUNT = 3; // Минимальный объем слива (литры)
    const MIN_REFILL_POINTS = 3; // Минимальное количество точек для заправки
    const MAX_REFILL_POINTS = 50; // Максимальное количество точек для заправки
    const MIN_DRAIN_POINTS = 2; // Минимальное количество точек для слива
    
    let i = 0;
    while (i < data.length - 2) {
      // Ищем заправки (рост топлива)
      let refillStart = i;
      let refillEnd = i;
      let maxIncrease = 0;
      let consecutiveIncreases = 0;
      
      // Ищем последовательный рост
      for (let j = i + 1; j < Math.min(i + MAX_REFILL_POINTS, data.length); j++) {
        const diff = data[j] - data[j - 1];
        
        if (diff > 0.2) { // Рост больше 0.2 литра
          consecutiveIncreases++;
          maxIncrease += diff;
          refillEnd = j;
        } else if (diff < -0.5) { // Падение больше 0.5 литра - конец заправки
          break;
        } else if (consecutiveIncreases > 0 && Math.abs(diff) < 0.1) {
          // Стабилизация после роста - продолжаем отслеживать
          refillEnd = j;
        } else if (consecutiveIncreases === 0) {
          // Нет роста в начале - не заправка
          break;
        }
      }
      
      // Если найдена заправка
      if (consecutiveIncreases >= MIN_REFILL_POINTS && maxIncrease >= MIN_REFILL_AMOUNT) {
        refills.push({
          startIndex: refillStart,
          endIndex: refillEnd,
          startTime: labels[refillStart],
          endTime: labels[refillEnd],
          amount: maxIncrease,
          startLevel: data[refillStart],
          endLevel: data[refillEnd]
        });
        i = refillEnd + 1;
        continue;
      }
      
      // Ищем сливы (падение топлива)
      let drainStart = i;
      let drainEnd = i;
      let maxDecrease = 0;
      let consecutiveDecreases = 0;
      
      // Ищем последовательное падение
      for (let j = i + 1; j < Math.min(i + 20, data.length); j++) {
        const diff = data[j] - data[j - 1];
        
        if (diff < -0.3) { // Падение больше 0.3 литра
          consecutiveDecreases++;
          maxDecrease += Math.abs(diff);
          drainEnd = j;
        } else if (diff > 0.2) { // Рост - конец слива
          break;
        } else if (consecutiveDecreases > 0 && Math.abs(diff) < 0.1) {
          // Стабилизация после падения
          drainEnd = j;
        } else if (consecutiveDecreases === 0) {
          break;
        }
      }
      
      // Если найден слив
      if (consecutiveDecreases >= MIN_DRAIN_POINTS && maxDecrease >= MIN_DRAIN_AMOUNT) {
        drains.push({
          startIndex: drainStart,
          endIndex: drainEnd,
          startTime: labels[drainStart],
          endTime: labels[drainEnd],
          amount: maxDecrease,
          startLevel: data[drainStart],
          endLevel: data[drainEnd]
        });
        i = drainEnd + 1;
        continue;
      }
      
      i++;
    }
    
    console.log('FuelChart: Найдено событий:', { 
      refills: refills.length, 
      drains: drains.length 
    });
    
    return { refills, drains };
  }, []);
  
  // Обработка данных о топливе и применение фильтра Калмана
  const processFuelData = useCallback((data) => {
    if (!data || !data.data || !Array.isArray(data.data) || data.data.length === 0) {
      setChartData([]);
      setChartLabels([]);
      setFilteredData([]);
      return;
    }
    
    // Сортируем данные по времени
    const sortedData = [...data.data].sort((a, b) => 
      new Date(a.time).getTime() - new Date(b.time).getTime()
    );
    
    // Извлекаем метки времени и значения уровня топлива
    const labels = sortedData.map(item => new Date(item.time));
    const fuelValues = sortedData.map(item => item.liters);
    
    // Применяем фильтр Калмана к данным
    // Сначала убедимся, что фильтр настроен с актуальными параметрами
    kalmanFilterRef.current.setParams(kalmanParams);
    kalmanFilterRef.current.reset(fuelValues[0] || 0); // Инициализируем первым значением
    
    // Применяем фильтрацию
    const filtered = kalmanFilterRef.current.filterArray(fuelValues);
    
    // Анализируем топливные события на отфильтрованных данных
    const events = analyzeFuelEvents(filtered, labels);
    
    // Сохраняем данные для графика
    setChartLabels(labels);
    setChartData(fuelValues);
    setFilteredData(filtered);
    setFuelEvents(events);
    
    console.log('FuelChart: Данные обработаны:', {
      labels: labels.length,
      rawValues: fuelValues.length,
      filteredValues: filtered.length,
      refills: events.refills.length,
      drains: events.drains.length
    });
  }, [kalmanParams, analyzeFuelEvents]);
  
  // Функция для обновления параметров фильтра Калмана
  // Примечание: функция не используется в текущей реализации, но оставлена для будущих улучшений
  // eslint-disable-next-line no-unused-vars
  const updateKalmanParams = useCallback((newParams) => {
    setKalmanParams(prevParams => {
      const updatedParams = { ...prevParams, ...newParams };
      console.log('FuelChart: Обновление параметров фильтра Калмана:', updatedParams);
      
      // Если у нас уже есть данные, применяем новые параметры фильтрации
      if (chartData.length > 0) {
        kalmanFilterRef.current.setParams(updatedParams);
        kalmanFilterRef.current.reset(chartData[0] || 0);
        const newFiltered = kalmanFilterRef.current.filterArray(chartData);
        setFilteredData(newFiltered);
      }
      
      return updatedParams;
    });
  }, [chartData]);
  
  // Переключение отображения исходных/отфильтрованных данных
  // Примечание: функция не используется в текущей реализации, но оставлена для будущих улучшений
  // eslint-disable-next-line no-unused-vars
  const toggleFiltered = useCallback(() => {
    setShowFiltered(prev => !prev);
  }, []);
  
  // Эффект для инициализации дат из localStorage и подписки на их изменение
  useEffect(() => {
    // Обработчик события изменения дат
    const handleDateRangeChanged = (event) => {
      try {
        const { startDate: newStartDate, endDate: newEndDate, period, timestamp, forceUpdate } = event.detail;
        
        // Проверяем, не слишком ли недавно мы сами обновили даты
        const now = new Date().getTime();
        const lastUpdate = window.lastDateUpdateTime || 0;
        
        // Если обновление было менее 1 секунды назад и это мы сами его сделали, игнорируем
        if ((now - lastUpdate) < 1000 && window.lastDateUpdateTime === timestamp) {
          console.log('FuelChart: Игнорируем событие dateRangeChanged - недавнее собственное обновление');
          return;
        }
        
        console.log('FuelChart: Получено событие изменения диапазона дат:', event.detail);
        
        // Если передан предустановленный период (неделя, месяц и т.д.)
        if (period) {
          console.log('FuelChart: Обработка предустановленного периода:', period);
          
          // Создаем даты на основе предустановленного периода
          let validStartDate = new Date();
          let validEndDate = new Date();
          
          switch(period) {
            case 'day':
              // Текущий день
              validStartDate.setHours(0, 0, 0, 0);
              break;
            case 'week':
              // Последние 7 дней
              validStartDate = new Date(validEndDate);
              validStartDate.setDate(validEndDate.getDate() - 7);
              break;
            case 'month':
              // Последние 30 дней
              validStartDate = new Date(validEndDate);
              validStartDate.setDate(validEndDate.getDate() - 30);
              break;
            case 'year':
              // Последние 365 дней
              validStartDate = new Date(validEndDate);
              validStartDate.setDate(validEndDate.getDate() - 365);
              break;
            default:
              // По умолчанию - неделя
              validStartDate = new Date(validEndDate);
              validStartDate.setDate(validEndDate.getDate() - 7);
          }
          
          // Сохраняем метку времени последнего обновления
          window.lastDateUpdateTime = timestamp || new Date().getTime();
          
          console.log('FuelChart: Установка дат на основе периода:', {
            start: validStartDate.toISOString(),
            end: validEndDate.toISOString()
          });
          
          setStartDate(validStartDate);
          setEndDate(validEndDate);
          return;
        }
        
        // Проверяем наличие дат или запрос на принудительное обновление
        if ((!newStartDate || !newEndDate) && forceUpdate) {
          // Получаем актуальные данные из localStorage
          const { startDate: storedStartDate, endDate: storedEndDate } = getDateRangeFromLocalStorage();
          
          if (storedStartDate && storedEndDate) {
            // Проверяем, изменились ли даты
            const startChanged = !startDate || 
              Math.abs(storedStartDate.getTime() - startDate.getTime()) > 1000;
            const endChanged = !endDate || 
              Math.abs(storedEndDate.getTime() - endDate.getTime()) > 1000;
                
            if (startChanged || endChanged) {
              console.log('FuelChart: Установка дат из localStorage через forceUpdate:', {
                startDate: storedStartDate.toISOString(),
                endDate: storedEndDate.toISOString()
              });
              
              setStartDate(storedStartDate);
              setEndDate(storedEndDate);
            }
          }
          return;
        }
        
        // Обрабатываем полученные даты, если они есть
        if (newStartDate && newEndDate) {
          const validStartDate = ensureValidDate(newStartDate);
          const validEndDate = ensureValidDate(newEndDate);
          
          // Сохраняем метку времени последнего обновления
          window.lastDateUpdateTime = timestamp || new Date().getTime();
          
          // Проверяем, изменились ли даты
          const startChanged = !startDate || 
            Math.abs(validStartDate.getTime() - startDate.getTime()) > 1000;
          const endChanged = !endDate || 
            Math.abs(validEndDate.getTime() - endDate.getTime()) > 1000;
            
          if (startChanged || endChanged) {
            console.log('FuelChart: Установка дат из события:', {
              startDate: validStartDate.toISOString(),
              endDate: validEndDate.toISOString()
            });
            
            setStartDate(validStartDate);
            setEndDate(validEndDate);
          } else {
            console.log('FuelChart: Даты не изменились, обновление не требуется');
          }
        }
      } catch (error) {
        console.error('FuelChart: Ошибка при обработке события изменения дат:', error);
      }
    };
    
    // Обработчик события изменения дат внутри текущей вкладки
    const handleDateRangeChangedInTab = (event) => {
      try {
        const { startDate: newStartDate, endDate: newEndDate, timestamp } = event.detail;
        
        // Проверяем, не слишком ли недавно мы сами обновили даты
        const now = new Date().getTime();
        const lastUpdate = window.lastDateUpdateTime || 0;
        
        // Если обновление было менее 1 секунды назад и это мы сами его сделали, игнорируем
        if ((now - lastUpdate) < 1000 && window.lastDateUpdateTime === timestamp) {
          console.log('FuelChart: Игнорируем событие dateRangeChangedInTab - недавнее собственное обновление');
          return;
        }
        
        console.log('FuelChart: Получено событие изменения дат в текущей вкладке:', {
          startDate: newStartDate instanceof Date ? newStartDate.toISOString() : newStartDate,
          endDate: newEndDate instanceof Date ? newEndDate.toISOString() : newEndDate
        });
        
        const validStartDate = ensureValidDate(newStartDate);
        const validEndDate = ensureValidDate(newEndDate);
        
        // Проверяем, изменились ли даты
        const startChanged = !startDate || 
          Math.abs(validStartDate.getTime() - startDate.getTime()) > 1000;
        const endChanged = !endDate || 
          Math.abs(validEndDate.getTime() - endDate.getTime()) > 1000;
          
        if (startChanged || endChanged) {
          console.log('FuelChart: Обновление дат из события в текущей вкладке:', {
            startDate: validStartDate.toISOString(),
            endDate: validEndDate.toISOString()
          });
          
          setStartDate(validStartDate);
          setEndDate(validEndDate);
        }
      } catch (error) {
        console.error('FuelChart: Ошибка при обработке события изменения дат в текущей вкладке:', error);
      }
    };
    
    // Обработчик события изменения в localStorage 
    const handleStorageChange = (event) => {
      if (event.key === 'lastDateRange') {
        try {
          const newValue = JSON.parse(event.newValue);
          
          // Если данных нет, выходим
          if (!newValue || !newValue.startDate || !newValue.endDate) {
            return;
          }
          
          const validStartDate = ensureValidDate(newValue.startDate);
          const validEndDate = ensureValidDate(newValue.endDate);
          
          // Проверяем, изменились ли даты
          const startChanged = !startDate || 
            Math.abs(validStartDate.getTime() - startDate.getTime()) > 1000;
          const endChanged = !endDate || 
            Math.abs(validEndDate.getTime() - endDate.getTime()) > 1000;
            
          if (startChanged || endChanged) {
            console.log('FuelChart: Установка дат из localStorage:', {
              startDate: validStartDate.toISOString(),
              endDate: validEndDate.toISOString()
            });
            
            setStartDate(validStartDate);
            setEndDate(validEndDate);
          }
        } catch (error) {
          console.error('FuelChart: Ошибка при обработке изменения localStorage:', error);
        }
      }
    };
    
    // Обработчик события изменения выбранного ТС
    const handleVehicleChanged = (event) => {
      console.log('FuelChart: Получено событие смены транспорта:', event.detail);
      // Если это событие обновления данных после выбора ТС, то просто загружаем данные заново
      if (vehicle && startDate && endDate) {
        console.log('FuelChart: Перезагрузка данных после смены транспорта');
        fetchFuelData();
      }
    };
    
    // Обработчик события принудительного обновления графиков
    const handleForceUpdate = () => {
      console.log('FuelChart: Получено событие принудительного обновления');
      if (vehicle && startDate && endDate) {
        fetchFuelData();
      }
    };
    
    // Добавляем слушатели событий
    window.addEventListener('dateRangeChanged', handleDateRangeChanged);
    window.addEventListener('dateRangeChangedInTab', handleDateRangeChangedInTab);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('vehicleChanged', handleVehicleChanged);
    window.addEventListener('forceUpdateCharts', handleForceUpdate);
    
    // Инициализация - проверяем, есть ли даты в состоянии, если нет - берем из localStorage
    if (!startDate || !endDate) {
      const { startDate: storedStartDate, endDate: storedEndDate } = getDateRangeFromLocalStorage();
      
      if (storedStartDate && storedEndDate) {
        console.log('FuelChart: Инициализация дат из localStorage:', {
          startDate: storedStartDate.toISOString(),
          endDate: storedEndDate.toISOString()
        });
        
        setStartDate(storedStartDate);
        setEndDate(storedEndDate);
      } else {
        // Если в localStorage нет дат, устанавливаем последнюю неделю
        const now = new Date();
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        
        console.log('FuelChart: Установка дат по умолчанию (неделя):', {
          startDate: weekAgo.toISOString(),
          endDate: now.toISOString()
        });
        
        setStartDate(weekAgo);
        setEndDate(now);
      }
    }
    
    return () => {
      // Удаляем слушатели событий при размонтировании
      window.removeEventListener('dateRangeChanged', handleDateRangeChanged);
      window.removeEventListener('dateRangeChangedInTab', handleDateRangeChangedInTab);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('vehicleChanged', handleVehicleChanged);
      window.removeEventListener('forceUpdateCharts', handleForceUpdate);
      
      // Отменяем все запросы при размонтировании
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [ensureValidDate, getDateRangeFromLocalStorage, startDate, endDate, vehicle, fetchFuelData]);
  
  // Загрузка данных при изменении дат или ТС
  useEffect(() => {
    if (vehicle && startDate && endDate) {
      console.log('FuelChart: Загрузка данных при изменении зависимостей:', { 
        vehicle: vehicle.imei,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      fetchFuelData();
    }
  }, [vehicle, startDate, endDate, fetchFuelData]);
  
  // Форматирование метки для оси Y (значение топлива)
  const formatFuelLabel = (value) => `${value} л`;
  
  // Форматирование метки времени для оси X
  const formatTimeLabel = (dateTime) => {
    return formatChartTimeLabel(dateTime, startDate, endDate);
  };
  
  // Создаем данные для графика с двумя линиями
  const getChartConfig = () => {
    return {
      labels: chartLabels,
      datasets: [
        {
          label: 'Фильтр Калмана',
          data: showFiltered ? filteredData : [],
          fill: false,
          backgroundColor: 'rgba(255, 64, 64, 0.2)',
          borderColor: 'rgb(255, 64, 64)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          tension: 0,
        },
        {
          label: 'Исходные данные',
          data: chartData,
          fill: false,
          backgroundColor: 'rgba(64, 80, 255, 0.2)',
          borderColor: 'rgb(64, 80, 255)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          tension: 0,
        }
       
      ]
    };
  };
  
  // Настройки графика
  const getChartOptions = () => {
    // Создаем аннотации для заправок и сливов
    const annotations = [];
    
    // Добавляем зоны заправок (зеленый фон)
    fuelEvents.refills.forEach((refill) => {
      annotations.push({
        type: 'box',
        xMin: refill.startTime,
        xMax: refill.endTime,
        backgroundColor: 'rgba(34, 197, 94, 0.2)', // Зеленый прозрачный
        borderColor: 'rgba(34, 197, 94, 0.8)',
        borderWidth: 1,
        label: {
          content: `Заправка: +${refill.amount.toFixed(1)}л`,
          enabled: true,
          position: 'top',
          backgroundColor: 'rgba(34, 197, 94, 0.9)',
          color: 'white',
          font: {
            size: 11
          }
        }
      });
    });
    
    // Добавляем зоны сливов (оранжевый фон)
    fuelEvents.drains.forEach((drain) => {
      annotations.push({
        type: 'box',
        xMin: drain.startTime,
        xMax: drain.endTime,
        backgroundColor: 'rgba(251, 146, 60, 0.2)', // Оранжевый прозрачный
        borderColor: 'rgba(251, 146, 60, 0.8)',
        borderWidth: 1,
        label: {
          content: `Слив: -${drain.amount.toFixed(1)}л`,
          enabled: true,
          position: 'bottom',
          backgroundColor: 'rgba(251, 146, 60, 0.9)',
          color: 'white',
          font: {
            size: 11
          }
        }
      });
    });
    
    return {
      plugins: {
        annotation: {
          annotations: annotations
        }
      },
      tooltips: {
        mode: 'index',
        callbacks: {
          title: (tooltipItems) => {
            if (tooltipItems.length > 0) {
              const dateTime = tooltipItems[0].xLabel;
              if (dateTime && dateTime instanceof Date) {
                return formatChartTooltipTime(dateTime);
              }
            }
            return '';
          }
        }
      }
    };
  };
  
  return (
    <>
      {/* Панель управления фильтром Калмана */}
      {/* {showFiltered && (
        <div className="kalman-filter-controls">
          <div className="filter-header">
            <h4>Параметры фильтра Калмана</h4>
            <label className="toggle-switch">
              <input 
                type="checkbox" 
                checked={showFiltered} 
                onChange={() => setShowFiltered(prev => !prev)} 
              />
              <span className="slider"></span>
              <span className="toggle-label">Показать фильтрацию</span>
            </label>
          </div>
        </div>
      )} */}
      
      <BaseChart
        title="Уровень топлива"
        vehicle={vehicle}
        startDate={startDate}
        endDate={endDate}
        data={getChartConfig()}
        fetchData={fetchFuelData}
        formatTooltipLabel={formatFuelLabel}
        formatYAxisLabel={formatFuelLabel}
        formatXAxisLabel={formatTimeLabel}
        emptyDataMessage="Нет данных о топливе за выбранный период"
        isLoading={isLoading}
        error={error}
        options={getChartOptions()}
        reportType="fuel"
      />
    </>
  );
};

export default FuelChart; 